//@ts-check
import React, { useEffect, useMemo, useState } from 'react'
import { getClassSessionsByDay } from 'api/v2/classSessions'
import DatePicker from 'components/inputs/DatePicker'
import DotsSpinner from 'components/spinners/DotsSpinner'
import { H2, Paragraph } from 'components/typography'
import {
  ClassSessionStatusLabels,
  NormalizedLevelLabels
} from 'utils/constants'
import { addDays, DEFAULT_UNIT_HOURS } from 'utils/date'
import styles from './Planning.module.css'

const HOUR_SLOTS = [...DEFAULT_UNIT_HOURS.am, ...DEFAULT_UNIT_HOURS.pm]

function parseSlot(slot) {
  const [start, end] = slot.split(' - ')
  const [sh, sm] = start.split(':').map(Number)
  const [eh, em] = end.split(':').map(Number)
  return { startMinutes: sh * 60 + sm, endMinutes: eh * 60 + em }
}

function sessionMinutes(date) {
  const d = new Date(date)
  return d.getHours() * 60 + d.getMinutes()
}

function findCurrentSlot() {
  const now = new Date()
  const minutes = now.getHours() * 60 + now.getMinutes()
  const slot = HOUR_SLOTS.find(s => {
    const { startMinutes, endMinutes } = parseSlot(s)
    return minutes >= startMinutes && minutes < endMinutes
  })
  return slot || HOUR_SLOTS[0]
}

function groupSessionsBySlot(sessions) {
  const grouped = HOUR_SLOTS.reduce((acc, slot) => {
    acc[slot] = []
    return acc
  }, {})
  sessions.forEach(session => {
    const start = sessionMinutes(session.startTime)
    const end = sessionMinutes(session.endTime)
    HOUR_SLOTS.forEach(slot => {
      const { startMinutes, endMinutes } = parseSlot(slot)
      if (start < endMinutes && end > startMinutes) grouped[slot].push(session)
    })
  })
  return grouped
}

function fullName(person) {
  if (!person) return ''
  return [person.name, person.surname].filter(Boolean).join(' ')
}

function formatHour(value) {
  const d = new Date(value)
  const hh = d.getHours().toString().padStart(2, '0')
  const mm = d.getMinutes().toString().padStart(2, '0')
  return `${hh}:${mm}`
}

function formatHourRange(start, end) {
  return `${formatHour(start)} - ${formatHour(end)}`
}

function translateStatus(status) {
  return ClassSessionStatusLabels[status] || status
}

function getFriendlyErrorMessage(error) {
  const status = error?.statusCode
  if (status === 404)
    return 'Esta funcionalidad aún no está disponible. Estará lista en breve.'
  if (status === 401 || status === 403)
    return 'Tu sesión ha caducado. Vuelve a iniciar sesión para continuar.'
  if (status >= 500)
    return 'Estamos teniendo problemas para cargar tus clases. Inténtalo en unos minutos.'
  return 'No se han podido cargar las clases. Inténtalo de nuevo.'
}

function SessionRow({ session, isOpen, onToggle }) {
  const hasObservations = !!session.observations
  return (
    <>
      <tr className={styles.row}>
        <td className={styles.cell}>{fullName(session.student)}</td>
        <td className={styles.cell}>{session.subject?.name || ''}</td>
        <td className={styles.cell}>
          {NormalizedLevelLabels[session.level] || '—'}
        </td>
        <td className={styles.cell}>
          {formatHourRange(session.startTime, session.endTime)}
        </td>
        <td className={[styles.cell, styles.cellCenter].join(' ')}>
          <span
            className={[
              styles.statusBadge,
              styles[`status_${session.status}`]
            ].join(' ')}
          >
            {translateStatus(session.status)}
          </span>
        </td>
        <td className={[styles.cell, styles.cellToggle].join(' ')}>
          {hasObservations && (
            <button
              type='button'
              className={[
                styles.toggleButton,
                isOpen ? styles.toggleOpen : ''
              ].join(' ')}
              onClick={onToggle}
              aria-label='Ver observaciones'
            >
              ▾
            </button>
          )}
        </td>
      </tr>
      {hasObservations && isOpen && (
        <tr className={styles.detailRow}>
          <td colSpan={6} className={styles.detailCell}>
            <Paragraph type='body2'>
              <strong>Observaciones: </strong>
              {session.observations}
            </Paragraph>
          </td>
        </tr>
      )}
    </>
  )
}

function SessionTable({ sessions, openId, onToggle }) {
  if (!sessions.length)
    return (
      <div className={styles.emptySlot}>
        <Paragraph type='body1'>No hay clases en esta franja.</Paragraph>
      </div>
    )

  const sorted = [...sessions].sort((a, b) =>
    fullName(a.student).localeCompare(fullName(b.student))
  )

  return (
    <table className={styles.table}>
      <thead>
        <tr>
          <th className={styles.th}>Alumno</th>
          <th className={styles.th}>Asignatura</th>
          <th className={styles.th}>Nivel</th>
          <th className={styles.th}>Horario</th>
          <th className={[styles.th, styles.cellCenter].join(' ')}>Estado</th>
          <th className={[styles.th, styles.cellToggle].join(' ')} />
        </tr>
      </thead>
      <tbody>
        {sorted.map(session => (
          <SessionRow
            key={session.id}
            session={session}
            isOpen={openId === session.id}
            onToggle={() => onToggle(session.id)}
          />
        ))}
      </tbody>
    </table>
  )
}

function Planning() {
  const [date, setDate] = useState(new Date())
  const [sessions, setSessions] = useState([])
  const [isFetching, setIsFetching] = useState(false)
  const [error, setError] = useState(null)
  const [activeSlot, setActiveSlot] = useState(findCurrentSlot())
  const [openSessionId, setOpenSessionId] = useState(null)

  useEffect(() => {
    if (!date) return
    let cancelled = false
    setIsFetching(true)
    setError(null)
    getClassSessionsByDay({ date })
      .then(data => {
        if (cancelled) return
        setSessions(Array.isArray(data) ? data : [])
      })
      .catch(e => {
        if (cancelled) return
        setError(getFriendlyErrorMessage(e))
        setSessions([])
      })
      .finally(() => {
        if (!cancelled) setIsFetching(false)
      })
    return () => {
      cancelled = true
    }
  }, [date])

  const groupedSessions = useMemo(
    () => groupSessionsBySlot(sessions),
    [sessions]
  )

  const handleDateChange = e => {
    const next = e?.target?.value
    if (next) setDate(next)
  }

  const shiftDate = days => setDate(prev => addDays(prev || new Date(), days))

  const handleToggleSession = id => {
    setOpenSessionId(prev => (prev === id ? null : id))
  }

  return (
    <section className={styles.section}>
      <header className={styles.header}>
        <H2>Planificación</H2>
        <div className={styles.dateControls}>
          <button
            type='button'
            className={styles.chevron}
            onClick={() => shiftDate(-1)}
            aria-label='Día anterior'
          >
            ‹
          </button>
          <div className={styles.datePicker}>
            <DatePicker
              name='date'
              value={date}
              isClearable={false}
              onChange={handleDateChange}
            />
          </div>
          <button
            type='button'
            className={styles.chevron}
            onClick={() => shiftDate(1)}
            aria-label='Día siguiente'
          >
            ›
          </button>
        </div>
      </header>

      {error ? (
        <div className={styles.errorState}>
          <Paragraph type='body1'>{error}</Paragraph>
        </div>
      ) : (
        <>
          <div className={styles.tabs}>
            {HOUR_SLOTS.map(slot => {
              const count = groupedSessions[slot]?.length || 0
              return (
                <div
                  key={slot}
                  className={[
                    styles.tab,
                    activeSlot === slot ? styles.active : ''
                  ].join(' ')}
                  onClick={() => setActiveSlot(slot)}
                >
                  <span className={styles.tabSlot}>{slot}</span>
                  {count > 0 && (
                    <span className={styles.tabCount}>{count}</span>
                  )}
                </div>
              )
            })}
          </div>

          <div className={styles.tabContent}>
            {isFetching ? (
              <div className={styles.loader}>
                <DotsSpinner />
              </div>
            ) : (
              <SessionTable
                sessions={groupedSessions[activeSlot] || []}
                openId={openSessionId}
                onToggle={handleToggleSession}
              />
            )}
          </div>
        </>
      )}
    </section>
  )
}

export default Planning
